import { useEffect, useState } from 'react';
import heroPayrollManagement from '../../../assets/auth-hero-payroll-management.webp';
import heroRelaxedProfessional from '../../../assets/auth-hero-relaxed-professional.webp';
import heroTeamCelebrating from '../../../assets/auth-hero-team-celebrating.webp';

export interface Slide {
  /** EPN-36 — omit both (a bare photo, no scrim/headline/body/dots) for a screen whose design has no
   * marketing copy at all, e.g. the Partner reset-password screens. Every Admin/Partner Sign In slide
   * sets both. */
  headline?: string;
  body?: string;
  /** Original Figma-exported photography (see this component's own doc comment below) — every slide has
   * one; none is intended as gradient-only. */
  image: string;
  /** CSS `background-position` (this component's equivalent of `object-position`) keeping each photo's
   * own focal point — the subject's face — in frame after this card's own aspect ratio (see
   * AuthShellLayout.css's `.auth-shell__hero-card`) crops the source image's native 1033×1205, since that
   * ratio doesn't exactly match every viewport width. Chosen per-image, not shared, because the three
   * source photos frame their subject differently (upper-left, upper-right-of-center, and a wider
   * mid-height group respectively). */
  focalPosition: string;
}

// Copy and photography taken from the approved "[Admin] - Sign In Flow" Figma handoff's three marketing
// frames.
const ADMIN_SLIDES: Slide[] = [
  {
    headline: 'Effortless Payroll Management',
    body: 'Process payroll faster and with confidence. Escochex Payroll automates calculations, deductions, and reports so you can focus on running your business.',
    image: heroPayrollManagement,
    focalPosition: '38% 18%',
  },
  {
    headline: 'Smarter Payroll Starts Here',
    body: 'Automate repetitive payroll tasks, minimize manual work, and ensure accurate salary processing with Escochex Payroll.',
    image: heroRelaxedProfessional,
    focalPosition: '62% 15%',
  },
  {
    headline: 'Payroll That Works for You',
    body: 'From employee records to payroll reports, Escochex Payroll helps businesses manage payroll efficiently and reliably.',
    image: heroTeamCelebrating,
    focalPosition: '50% 28%',
  },
];

const ROTATE_MS = 6000;

/**
 * Purely decorative marketing content — rendered `aria-hidden` by the parent (AuthShellLayout) since it
 * conveys no information a screen-reader user needs.
 *
 * Asset provenance: `auth-hero-payroll-management.webp`, `auth-hero-relaxed-professional.webp`, and
 * `auth-hero-team-celebrating.webp` are the ORIGINAL Figma-exported hero images supplied directly by
 * design (`hero-effortless-payroll-management.png`, `hero-smart-payroll-that-works.png`, and
 * `hero-payroll-that-works.png` respectively — re-encoded to .webp only, no crop/content change). These
 * replace an earlier, explicitly temporary stand-in that had been cropped out of screenshots pending the
 * real assets; that stand-in is gone now that the real files exist. `mask.png`, also supplied alongside
 * these, was inspected and found to be a fully transparent 1×1 pixel — it carries no usable crop/shape
 * data, so it was not used; the hero images' own native 1033×1205 crop was taken as the intended
 * composition instead (see AuthShellLayout.css's `.hero-carousel__photo` for the resulting
 * `object-position` tuning).
 */
export interface HeroCarouselProps {
  /** EPN-36 Partner Sign In override — default is Admin Sign In's own approved 3 slides, unchanged. */
  slides?: Slide[] | undefined;
}

export function HeroCarousel({ slides = ADMIN_SLIDES }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // A slide deck of 1 (or 0) has nothing to rotate to — avoids a pointless interval and, for 0, avoids
    // dividing by zero below.
    if (slides.length <= 1) {
      return;
    }
    // jsdom (this project's test environment) does not implement matchMedia — feature-detect rather
    // than assume it exists, so this component doesn't crash under test while still respecting a real
    // browser's reduced-motion preference.
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [slides]);

  if (slides.length === 0) {
    return null;
  }

  // index is always derived via `% slides.length` (or reset below), so it is always in range — the
  // non-null assertion reflects that invariant rather than papering over a real possibility of `undefined`.
  const slide = slides[index % slides.length]!;

  return (
    <div className="hero-carousel">
      <div
        className="hero-carousel__photo"
        style={{ backgroundImage: `url(${slide.image})`, backgroundPosition: slide.focalPosition }}
      />
      {slide.headline && (
        <>
          <div className="hero-carousel__scrim" />
          <div className="hero-carousel__content">
            <h2 className="hero-carousel__headline">{slide.headline}</h2>
            <p className="hero-carousel__body">{slide.body}</p>
            {slides.length > 1 && (
              <div className="hero-carousel__dots">
                {slides.map((_, i) => (
                  // Index is a stable key here: `slides` is a static, never-reordered prop for the
                  // lifetime of a given auth screen — this is a fixed indicator strip, not a dynamic
                  // collection.
                  <span key={i} className={`hero-carousel__dot${i === index ? ' hero-carousel__dot--active' : ''}`} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
